import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../api";

/**
 * Every bot, worker and watcher in one list, with whether it is running.
 * Each row reads the same status endpoint the bot's own panel does, so this
 * page and the panel cannot disagree; "Open" goes to the full panel.
 */

type Health = "healthy" | "idle" | "stale" | "failing" | "churning" | "unknown";

interface Bot {
  id: string;
  name: string;
  icon: string;
  does: string;
  endpoint: string;
  tool: string;
  schedule: string;
}

export const BOTS: Bot[] = [
  {
    id: "sync", name: "VectorSurv Sync Bot", icon: "🔄", endpoint: "/sync-status", tool: "vectorsurv-sync",
    does: "Keeps the surveillance and disease dashboard layers in step with VectorSurv.",
    schedule: "On a schedule, through the day",
  },
  {
    id: "verification", name: "Verification Alerts Bot", icon: "📬", endpoint: "/watcher-status", tool: "verification-watcher",
    does: "Emails the field supervisor when a verification inspection record is entered.",
    schedule: "On a schedule, working hours",
  },
  {
    id: "carcass", name: "Dead bird report watcher", icon: "🐦", endpoint: "/carcass-watch-status", tool: "carcass-report",
    does: "Emails Jovana when a new dead bird report falls inside the District or needs a look.",
    schedule: "Every half hour, every day",
  },
  {
    id: "abundance", name: "Abundance report watcher", icon: "🦟", endpoint: "/abundance-watch-status", tool: "abundance-report-data",
    does: "Emails Amy when a collection date is finished (two later dates entered) so she can run the report.",
    schedule: "Every half hour, Monday to Friday",
  },
];

const HEALTH: Record<Health, { label: string; dot: string; text: string }> = {
  healthy: { label: "Active", dot: "bg-green-500", text: "text-green-700" },
  idle: { label: "Idle", dot: "bg-slate-400", text: "text-slate-600" },
  stale: { label: "Stale", dot: "bg-amber-500", text: "text-amber-700" },
  churning: { label: "Churning", dot: "bg-amber-500", text: "text-amber-700" },
  failing: { label: "Failing", dot: "bg-red-500", text: "text-red-700" },
  unknown: { label: "Not installed", dot: "bg-slate-400", text: "text-slate-600" },
};

interface Result {
  health: Health;
  lastRun?: string | null;
  timerArmed?: boolean;
  problems: number;
  error?: string;
}

function summarise(d: any): Result {
  const when = d.last_run_local || d.last_run_at || null;
  return {
    health: (d.health as Health) || "unknown",
    lastRun: when ? String(when).replace("T", " ").slice(0, 16) : null,
    timerArmed: d.timer ? d.timer.armed : undefined,
    problems: (d.problems || []).length,
  };
}

export default function AutomationBotsPage() {
  const [results, setResults] = useState<Record<string, Result>>({});

  useEffect(() => {
    let live = true;
    const load = () =>
      BOTS.forEach((b) =>
        api
          .get(`${b.endpoint}?_=${Date.now()}`)
          .then((r) => live && setResults((p) => ({ ...p, [b.id]: summarise(r.data) })))
          .catch(() => live && setResults((p) => ({ ...p, [b.id]: { health: "unknown", problems: 0, error: "Could not read its status." } })))
      );
    load();
    const t = window.setInterval(load, 30000);
    return () => {
      live = false;
      window.clearInterval(t);
    };
  }, []);

  const attention = BOTS.filter((b) => {
    const r = results[b.id];
    return r && (r.health === "failing" || r.health === "stale" || r.timerArmed === false);
  }).length;

  return (
    <div className="space-y-4" data-testid="automation-bots">
      <div>
        <h1 className="text-2xl font-extrabold">Automation Bots</h1>
        <p className="text-ink-soft text-[14px] mt-1">
          The jobs that run on the server by themselves: what each one does, whether it is running, and when it last ran.
          Open one for its full activity, recipients and history.
        </p>
      </div>

      <div className={`rounded-lg p-3 text-[13px] ${attention ? "bg-red-50 border border-red-200 text-red-800" : "bg-brand-tint text-brand-deep"}`}>
        {Object.keys(results).length < BOTS.length
          ? "Checking…"
          : attention
          ? `${attention} of ${BOTS.length} need attention.`
          : `All ${BOTS.length} are running as expected.`}
      </div>

      <div className="grid gap-3 min-[720px]:grid-cols-2">
        {BOTS.map((b) => {
          const r = results[b.id];
          const h = HEALTH[r?.health || "unknown"];
          return (
            <div key={b.id} className="ui-card p-4 flex flex-col gap-2" data-testid={`bot-${b.id}`}>
              <div className="flex items-center gap-2">
                <span className="text-xl">{b.icon}</span>
                <h2 className="font-extrabold text-[15px]">{b.name}</h2>
                <span className={`ml-auto flex items-center gap-1.5 text-xs font-semibold ${h.text}`}>
                  <span className={`inline-block h-2.5 w-2.5 rounded-full ${r ? h.dot : "bg-slate-300"}`} />
                  {r ? h.label : "…"}
                </span>
              </div>
              <p className="text-[13px] text-ink-soft">{b.does}</p>
              <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-[12px]">
                <div>
                  <dt className="text-slate-500">Last run</dt>
                  <dd className="font-medium">{r?.lastRun || "—"}</dd>
                </div>
                <div>
                  <dt className="text-slate-500">Schedule</dt>
                  <dd className="font-medium">{b.schedule}</dd>
                </div>
              </dl>
              {r?.timerArmed === false && <p className="text-[12px] text-red-700">Its timer is not armed.</p>}
              {r?.problems ? <p className="text-[12px] text-red-700">{r.problems} problem(s) reported.</p> : null}
              {r?.error && <p className="text-[12px] text-wnv">{r.error}</p>}
              <Link to={`/tools/${b.tool}`} className="btn-ghost text-xs !py-1 self-start mt-auto">
                Open
              </Link>
            </div>
          );
        })}
      </div>

      <p className="text-[12px] text-ink-faint">
        Also on the server, with no page of their own: the nightly database backup and weekly restore test, the nightly
        mirror check, and the alert checker that emails when any of the bots above stops.
      </p>
    </div>
  );
}
